/** This is a bundle offer search tile
 *  This tile will first check whether (1) the search performed are property search and (2) the result has been soldout
 *  Then it will render either the (1) Expansed or (2) Condensed search results.
 * */

import { FormattedMessage, useIntl } from 'react-intl'
import productBadgeMessages from 'i18n/productBadgeMessages'
import { useMemo } from 'react'
import styled from 'styled-components'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { rem } from 'polished'
import useBestPriceBundleAndSavePackage from 'hooks/useBestPriceBundleAndSavePackage'
import BodyText from 'LuxKit/Typography/BodyText'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import { sum } from 'lib/array/arrayUtils'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import TextButton from 'LuxKit/Button/TextButton'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import Group from 'components/utils/Group'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import { EmptyObject } from 'lib/object/objectUtils'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  padding: ${rem(16)};
`

const StyledGroup = styled(Group)`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  > * + * {
    width: fit-content;
  }
`

interface Props {
  offer: App.Offer | App.OfferSummary
  bundleOffer: App.BundleOffer
  filters?: App.OfferListFilters
  offerLinkIncludesFilters?: boolean
}

function ViewBundleOfferContent({
  offer,
  bundleOffer,
  filters = EmptyObject,
  offerLinkIncludesFilters,
}: Props) {
  const intl = useIntl()
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const {
    bestPricePackage,
    bestPriceError,
    fetching,
    soldOut,
  } = useBestPriceBundleAndSavePackage(bundleOffer, offer.id, filters)

  const offerUrl = useOfferUrl(bundleOffer, {
    filters,
    bundledOfferId: offer.id,
    offerLinkIncludesFilters,
  })

  const priceMultiplier = (filters?.rooms?.length || 1)
  const extraGuestSurcharges = useMemo(() => bestPricePackage ?
      sum(bestPricePackage.packages.map(p =>
        getSumExtraGuestsSurcharge(filters.rooms || [], bundleOffer.bundledOffers[p.offerId], p),
      )) : 0, [bestPricePackage, filters.rooms, bundleOffer])

  const propertyFees = (bestPricePackage?.propertyFees || 0) * priceMultiplier

  const hotelPrice = (((bestPricePackage?.price ?? 0) + (bestPricePackage?.surcharge ?? 0)) * priceMultiplier) + extraGuestSurcharges
  const baseTotalPrice = hotelPrice + propertyFees

  const {
    saleUnit,
    total: totalPrice,
  } = useOfferPrice({
    value: bestPricePackage?.value,
    saleUnit: 'total',
    duration: bestPricePackage?.duration,
    total: baseTotalPrice,
  })

  if (!bestPricePackage || bestPriceError || fetching || soldOut) {
    return null
  }

  const secondOfferPackage = bestPricePackage.packages.find(x => x.offerId !== offer.id)
  const secondOffer = Object.values(bundleOffer.bundledOffers).find(x => x.id !== offer.id)

  const { shouldShowDiscountPercent, hotelDiscountPercent } = getBundleValueUp(bundleOffer, bestPricePackage)

  const linkParams = {
    to: offerUrl,
    ...(isOpenNewTabOfferClickEnabled ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
  }

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <ClickableLink {...linkParams}>
        <Container>
          <StyledGroup direction="vertical" gap={8} fullHeight fullWidth>
            <Group direction="vertical" gap={8}>
              <div>
                <HighlightLabel>
                  {intl.formatMessage(productBadgeMessages.bundleAndSave)}
                </HighlightLabel>
              </div>
              {secondOfferPackage && (
                <BodyText variant="medium">
                  <FormattedMessage
                    id="9gt_B4_hKn26Ka0FKuCW"
                    defaultMessage="Great savings available when you bundle this hotel with a {durationLabel} stay at <b>{propertyName}</b>"
                    
                    values={{
                      durationLabel: intl.formatMessage(
                        {
                          id: '5a29unaFphsAjEpSkmeP',
                          defaultMessage: '{duration, plural, one {# night} other {# nights}}',
                        },
                        { duration: secondOfferPackage.duration },
                      ),
                      propertyName: secondOffer?.property.name,
                      b: (chunks) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>,
                    }}
                  />
                </BodyText>
              )}
              <PriceRowPriceWithCaption
                size="S"
                caption={intl.formatMessage(
                  {
                    id: 'txEAg6DRUR05boyc1aZ-',
                    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                  },
                  { count: bestPricePackage.duration },
                )}
                price={totalPrice}
                saleUnit={saleUnit}
                discountPercentage={shouldShowDiscountPercent ? hotelDiscountPercent : undefined}
              />
            </Group>
            <Group direction="horizontal" horizontalAlign="end" fullWidth>
              <TextButton fit="mobile-full-width" kind="secondary">{intl.formatMessage({
                id: 'Ygo1PSwKpsqYra5zEJ1O',
                defaultMessage: 'View bundle offer',
              })}
              </TextButton>
            </Group>
          </StyledGroup>
        </Container>
      </ClickableLink>
    </ErrorBoundary>
  )
}

export default ViewBundleOfferContent
